import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../ui/toast';
import { AUTH_KEYS, NS } from '../../i18n/constants/keys';
import defaultCam from '../../assets/images/graphics/default-cam.svg';
import personOutline from '../../assets/images/graphics/person.svg';
import logoBlackText from '../../assets/images/logos/white-bg-logo-hiacuity.png';
import LogoWhite from '../../assets/images/logos/white-bg-logo-hiacuity.png';
import { Button } from '../../ui';
import { GreenTick, RedCross } from '../../ui/icons/icons';
import { SecondaryTypography } from '../../ui/typography/typography';
import {
  awsUpload,
  getStorageLink,
} from '../../utils/api_service/storage_api';
import { updateProfilePic } from '../../utils/api_service/user_api';
import { getPermissions } from '../../utils/helpers/auth';
import { getImageSrc } from '../../utils/helpers/image-utils';
import { RootState } from '../../utils/redux_store/store';
import { IS_IOS } from '../assessment/perform/assigned_assessments/assigned_assessments';
import { AuthenticationUiLayout } from './AuthenticationUILayout';
import { CompanyLogoBadge } from './CompanyLogoBadge';
import styles from './login_register.module.css';

export const IdentityCheck = () => {
  const history = useHistory();
  const { t } = useTranslation(NS.auth);
  const videoRef = useRef<HTMLVideoElement>(null);
  const photoRef = useRef<HTMLCanvasElement>(null);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [picDisabled, setPicDisabled] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    getUserCamera();
  }, [videoRef]);

  useEffect(() => {
    const processImage = async () => {
      if (isProcessing) {
        const video = videoRef.current;
        const photo = photoRef.current;
        if (video && photo) {
          const ctx = photo.getContext('2d');
          photo.width = 400;
          photo.height = video.videoHeight;
          ctx?.drawImage(video, 0, 0, 400, 480);
          // const dataURL = photo.toDataURL('image/png');
          setDisabled(false);
          setVerified(true);
        }
        setIsProcessing(false);
        setPicDisabled(false);
      }
    };
    processImage();
  }, [isProcessing]);

  useEffect(() => {
    const canvas = photoRef.current;
    const video = videoRef.current;
    const image = new Image();
    if (canvas && video) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = 200;
        canvas.height = 240;
        image.src = defaultCam;
        image.onload = () => {
          ctx.drawImage(image, 0, 0, 200, 240);
        };
      }
    }
  }, []);

  const stopCamera = () => {
    const video = videoRef.current;
    if (video && video.srcObject) {
      const stream = video.srcObject as MediaStream;
      stream.getTracks().forEach(track => {
        track.stop();
      });
      video.srcObject = null;
    }
  };

  const getUserCamera = () => {
    navigator.mediaDevices
      .getUserMedia({
        video: true,
      })
      .then((stream) => {
        let video = videoRef.current;
        if (video) {
          video.muted = true;
          video.srcObject = stream;
          video.autoplay = true;
        }
      });
  };

  const takePicture = async () => {
    setIsProcessing(true);
    setPicDisabled(true);
  };

  const uploadPicture = async (
    fileName: string,
    folderName: string,
    image: Blob,
  ) => {
    try {
      const storageLinkResponse = await getStorageLink(fileName, folderName);
      await awsUpload(storageLinkResponse.url, image);
      const updateProfilePicResponse = await updateProfilePic(
        storageLinkResponse.fileName,
      );

      if (updateProfilePicResponse.id) {
        stopCamera(); // Stop camera before navigation

        if (localStorage.getItem('id')) {
          localStorage.setItem('profilePic', updateProfilePicResponse.profilePic);
          forwardByRole();
        } else {
          localStorage.clear();
          history.push('/login');
        }
      } else {
        toast.error(t(AUTH_KEYS.identityCheck.errorSettingProfilePic), {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error(t(AUTH_KEYS.identityCheck.errorUploadingPicture), { position: 'bottom-center' });
    }
  };

  const capturePicture = (imageName: string) => {
    const folderName = 'profile-pic';
    const photo = photoRef.current;
    if (photo) {
      photo.toBlob(
        (blob) => {
          if (blob) {
            uploadPicture(imageName, folderName, blob);
          }
        },
        'image/jpeg',
        0.9,
      );
    }
  };

  const forwardByRole = () => {
    const token = localStorage.getItem('token');
    const perms = token ? getPermissions(token) : [];
    history.push(perms.length === 0 ? '/assigned-assessments' : '/home');
  };

  const skip = () => {
    if (localStorage.getItem('id') || localStorage.getItem('email')) {
      forwardByRole();
    } else {
      localStorage.clear();
      history.push('/login');
    }
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName);

  return (
    <AuthenticationUiLayout leftContent={<></>}>
      <div className='flex items-start flex-col w-full gap-6 pt-10'>
        <div className='flex justify-between items-center w-full border-b pb-4'>
          <CompanyLogoBadge
              logoUrl={companyLogo}
              companyName={companyName}
              altText='HiAcuity Logo'
          />
          <div>
            <Button
                onClick={() => skip()}
                variant={'secondary'}
                label={t(AUTH_KEYS.identityCheck.skip)}
                width='w-24'
            />
          </div>
        </div>
        <h1 className='text-2xl md:text-4xl font-medium'>{ t(AUTH_KEYS.identityCheck.termsTitle) }</h1>
      </div>

      <h1 className={styles.formHeading}>{ t(AUTH_KEYS.identityCheck.title) }</h1>
      <p>
        { t(AUTH_KEYS.identityCheck.instruction) }
      </p>

      <div className='w-full flex flex-col sm:flex-row justify-center items-start gap-8'>
        { /* Video with overlay and button */ }
        <div className='relative flex flex-col items-center'>
          <div className='relative'>
            { IS_IOS ? (
              <video
                  className='mt-2 mb-2 object-fill w-[48vw] h-[25vh] sm:w-[34vw] sm:h-[25vh] md:w-[36vw] md:h-[25vh] lg:w-[11vw] lg:h-[25vh] border border-black'
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
              />
            ) : (
              <video
                  className='mt-2 mb-2 object-fill w-[48vw] h-[25vh] sm:w-[34vw] sm:h-[25vh] md:w-[36vw] md:h-[25vh] lg:w-[11vw] lg:h-[25vh] border border-black'
                  ref={videoRef}
                  autoPlay
                  playsInline
              />
            ) }

            { /* Overlay person outline centered on video */ }
            <div className='absolute inset-0 flex justify-center items-center pointer-events-none'>
              <img
                  src={personOutline}
                  alt='Person Outline'
                  className='w-full h-auto transform scale-125'
              />
            </div>
          </div>

          <div>
            <Button
                onClick={() => takePicture()}
                variant={'primary'}
                label={t(AUTH_KEYS.identityCheck.takePicture)}
                disabled={picDisabled}
            />
          </div>
        </div>

        { /* Canvas and verification indicators */ }
        <div className='relative flex flex-col items-center'>
          <canvas
              className='mt-2 mb-2 object-fill w-[48vw] h-[25vh] sm:w-[34vw] sm:h-[25vh] md:w-[36vw] md:h-[25vh] lg:w-[11vw] lg:h-[25vh] border border-black'
              ref={photoRef}
          ></canvas>

          { isProcessing && (
            <div
                className='text-center absolute left-1/2 transform -translate-x-1/2 p-4 rounded font-bold w-64'
                style={{ top: '85%' }}
            >
              { t(AUTH_KEYS.identityCheck.processingImage) }
              <br />
              { t(AUTH_KEYS.identityCheck.pleaseWait) }
            </div>
          ) }

          <div className='mt-2.5 flex flex-col gap-2.5'>
            <div className='flex gap-2.5'>
              <SecondaryTypography.Medium fontWeight='semi-bold'>
                { t(AUTH_KEYS.identityCheck.fullFace) }
              </SecondaryTypography.Medium>
              { verified ? <GreenTick /> : <RedCross /> }
            </div>
            <div className='flex gap-2.5'>
              <SecondaryTypography.Medium fontWeight='semi-bold'>
                { t(AUTH_KEYS.identityCheck.lighting) }
              </SecondaryTypography.Medium>
              { verified ? <GreenTick /> : <RedCross /> }
            </div>
          </div>
        </div>
      </div>

      { /* Final action button */ }
      <div className='mt-10 flex justify-end items-center border-t pb-20'>
        <Button
            onClick={() => capturePicture('test.jpg')}
            label={t(AUTH_KEYS.identityCheck.completeRegistration)}
            variant='primary'
            disabled={disabled}
        />
      </div>
    </AuthenticationUiLayout>
  );
};
